/**
 * MusicPlaylistPicker - 曲をプレイリストへ追加するボトムシート
 *
 * 各曲メニューから開くため、実体は MiniPlayer 側で1つだけマウントし、
 * 開閉と対象の曲は usePlaylistPicker で受け取る。
 */

import { Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { addMusicPlaylistTracks, createMusicPlaylist, getMusicPlaylists } from '@/api/api'
import type { MusicPlaylistItem } from '@/api/type'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { useSubmitLock } from '@/hooks/useSubmitLock'

import { ACCENT_COLOR } from './constants'
import { PlaylistRow } from './playlist-row'
import { usePlaylistPicker } from './use-playlist-picker'

export const MusicPlaylistPicker = () => {
  const episodeIds = usePlaylistPicker((s) => s.episodeIds)
  const closePlaylistPicker = usePlaylistPicker((s) => s.closePlaylistPicker)

  const [playlists, setPlaylists] = useState<MusicPlaylistItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const { isSubmitting, run } = useSubmitLock()

  const isOpen = episodeIds !== null

  useEffect(() => {
    if (!isOpen) return

    let cancelled = false
    setIsCreating(false)
    setNewName('')
    setIsLoading(true)
    getMusicPlaylists()
      .then((res) => {
        if (!cancelled) setPlaylists(res.playlists)
      })
      .catch(() => {
        if (!cancelled) toast.error('プレイリストの取得に失敗しました')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen])

  const handleAdd = (playlist: MusicPlaylistItem) => {
    if (episodeIds === null) return

    void run(async () => {
      try {
        await addMusicPlaylistTracks(playlist.id, episodeIds)
        toast.success(`「${playlist.name}」に追加しました`)
        closePlaylistPicker()
      } catch {
        toast.error('プレイリストへの追加に失敗しました')
      }
    })
  }

  const handleCreate = () => {
    const name = newName.trim()
    if (episodeIds === null || !name) return

    void run(async () => {
      try {
        await createMusicPlaylist(name, episodeIds)
        toast.success(`「${name}」を作成しました`)
        closePlaylistPicker()
      } catch {
        toast.error('プレイリストの作成に失敗しました')
      }
    })
  }

  return (
    <BottomSheet
      title="プレイリストに追加"
      open={isOpen}
      onOpenChange={(open) => !open && closePlaylistPicker()}
      snapPoints={[0.7]}
    >
      <div className="px-4 pb-8">
        {isCreating ? (
          <div className="flex gap-2 py-3">
            <Input
              autoFocus
              value={newName}
              maxLength={200}
              placeholder="プレイリスト名"
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            />
            <Button disabled={!newName.trim() || isSubmitting} onClick={handleCreate}>
              作成
            </Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setIsCreating(true)}
            className="flex w-full items-center gap-3 border-none py-4 text-left"
            style={{ color: ACCENT_COLOR }}
          >
            <Plus size={22} className="shrink-0" />
            新規プレイリスト
          </button>
        )}

        {isLoading && (
          <div className="flex justify-center py-8">
            <Spinner size={32} />
          </div>
        )}
        {!isLoading && playlists.length === 0 && (
          <div className="py-8 text-center text-muted-foreground">プレイリストがありません</div>
        )}
        {!isLoading &&
          playlists.map((playlist) => (
            <PlaylistRow key={playlist.id} playlist={playlist} onClick={() => handleAdd(playlist)} />
          ))}
      </div>
    </BottomSheet>
  )
}
